import forge from 'apis/forge';
import { Point } from 'dragonmouse-shared/geometry';
import { Frame, PartialFrame } from 'dragonmouse-shared/interfaces';
import { getFrames } from 'redux/db/util';
import type { Store } from 'redux/mode/reducers';
import store from 'redux/store';
import { Pick } from '../classes';
import { executeCommand } from '../commandManager';
import { MemorializeFrameUpdateCommand } from '../commands';

export type FrameSide = 'left' | 'right' | 'top' | 'bottom';

export const alignSelectedFrames = (side: FrameSide) => {
	const { selected } = store.getState().mode;
	const selectedFrames: Frame[] = getFrames(getSelectedFrameIds(selected));

	if (selectedFrames.length < 1) return;

	let alignValue = side === 'left' || side === 'top' ? Infinity : -Infinity;
	selectedFrames.forEach((f) => {
		switch (side) {
			case 'left':
				alignValue = Math.min(alignValue, f.x);
				break;
			case 'right':
				alignValue = Math.max(alignValue, f.x + f.width);
				break;
			case 'top':
				alignValue = Math.min(alignValue, f.y);
				break;
			case 'bottom':
				alignValue = Math.max(alignValue, f.y + f.height);
				break;
		}
	});

	const before: PartialFrame[] = [];
	const after: PartialFrame[] = [];
	selectedFrames.forEach((f) => {
		const update: PartialFrame = { id: f.id };
		switch (side) {
			case 'left':
				before.push({ id: f.id, x: f.x });
				after.push({ id: f.id, x: alignValue });
				update.x = alignValue;
				break;
			case 'right':
				before.push({ id: f.id, x: f.x });
				after.push({ id: f.id, x: alignValue - f.width });
				update.x = alignValue - f.width;
				break;
			case 'top':
				before.push({ id: f.id, y: f.y });
				after.push({ id: f.id, y: alignValue });
				update.y = alignValue;
				break;
			case 'bottom':
				before.push({ id: f.id, y: f.y });
				after.push({ id: f.id, y: alignValue - f.height });
				update.y = alignValue - f.height;
				break;
		}
		forge.updateFrame({ update });
	});
	executeCommand(new MemorializeFrameUpdateCommand(before, after));
};

export const moveSelectedFrames = (delta: Point) => {
	const { selected } = store.getState().mode;
	const selectedFrames: Frame[] = getFrames(getSelectedFrameIds(selected));

	const before: PartialFrame[] = [];
	const after: PartialFrame[] = [];
	selectedFrames.forEach((f) => {
		const beforeFrame: PartialFrame = { id: f.id, x: f.x, y: f.y };
		const afterFrame: PartialFrame = { id: f.id, x: f.x + delta.x, y: f.y + delta.y };
		before.push(beforeFrame);
		after.push(afterFrame);
		forge.updateFrame({ update: afterFrame });
	});
	executeCommand(new MemorializeFrameUpdateCommand(before, after));
};

export const framePicked = (picks: Pick[], frameId?: string): boolean => {
	if (!frameId) return false;
	return picks.find((s) => s.frameId && s.frameId === frameId && s.isFrame()) ? true : false;
};

export const getSelectedFrameId = (picks: Pick[]): string | null => {
	const selected = picks.filter((s) => s.frameId);
	if (selected.length === 1) return selected[0].frameId || null;
	return null;
};

export const getSelectedFrameIds = (picks: Pick[]): string[] => {
	return picks
		.filter((pick) => pick.isFrame() && pick.frameId)
		.map((pick) => pick.frameId) as string[];
};

export const getLeftPanelWidth = (state: Store) => {
	const { panelsVisible, leftPanelWidths, activeLeftPanelTab } = state;
	return panelsVisible ? leftPanelWidths[activeLeftPanelTab] : 0;
};
